import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { getOrganizationMedicalProfilesCacheKey, onDeleteOrganizationMedicalProfile } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationMedicalProfiles', {
    loadOrganizationMedicalProfilesFailure: 'Failed to load organization clients',
    loadOrganizationMedicalProfileFailure: 'Failed to load organization client',
    deleteOrganizationMedicalProfileSuccess: 'Organization client removed',
    deleteOrganizationMedicalProfileFailure: 'Failed to delete organization client',
});

export type UseOrganizationMedicalProfilesArgs = {
    medicalProfileId?: string;
    onDeleteOrganizationMedicalProfileSuccess?: () => void;
};

export type UseOrganizationMedicalProfilesReturnValue = {
    organizationMedicalProfiles: APITypes.organizationService.ListOrganizationMedicalProfilesResponse['items'];
    isLoadingOrganizationMedicalProfiles: boolean;
    isFetchingOrganizationMedicalProfiles: boolean;
    deleteOrganizationMedicalProfile: (medicalProfileId: string) => void;
    isDeletingOrganizationMedicalProfile: boolean;
    organizationMedicalProfile: APITypes.organizationService.GetOrganizationMedicalProfileResponse | null;
    isLoadingOrganizationMedicalProfile: boolean;
};

const emptyArray: any[] = [];

export default function useOrganizationMedicalProfiles({
    medicalProfileId,
    onDeleteOrganizationMedicalProfileSuccess,
}: UseOrganizationMedicalProfilesArgs = {}): UseOrganizationMedicalProfilesReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();
    const organizationMedicalProfilesQuery = useQuery({
        queryKey: getOrganizationMedicalProfilesCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationMedicalProfilesResponse>(
                urls.api.organizations.profiles.profiles(organizationId),
            );
        },
    });
    useQueryErrorNotify(organizationMedicalProfilesQuery, intl('loadOrganizationMedicalProfileFailure'));

    const organizationMedicalProfileQuery = useQuery({
        queryKey: medicalProfileId ? getOrganizationMedicalProfilesCacheKey(medicalProfileId) : [],
        queryFn: async () => {
            const value = await appAPI.get<APITypes.organizationService.GetOrganizationMedicalProfileResponse>(
                urls.api.organizations.profiles.profile(organizationId, medicalProfileId!),
            );

            return value;
        },
        enabled: !!medicalProfileId,
    });
    useQueryErrorNotify(organizationMedicalProfileQuery, intl('loadOrganizationMedicalProfileFailure'));

    const deleteOrganizationMedicalProfileMutation = useMutation({
        mutationFn: async (medicalProfileId: string) => {
            return await appAPI.del(urls.api.organizations.profiles.profile(organizationId, medicalProfileId));
        },
        onSuccess: () => {
            onDeleteOrganizationMedicalProfile.resetCache({})();
            notify('success', intl('deleteOrganizationMedicalProfileSuccess'));
            onDeleteOrganizationMedicalProfileSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteOrganizationMedicalProfileFailure'));
        },
    });

    return {
        organizationMedicalProfiles: organizationMedicalProfilesQuery.data?.items || emptyArray,
        isLoadingOrganizationMedicalProfiles: organizationMedicalProfilesQuery.isLoading,
        isFetchingOrganizationMedicalProfiles: organizationMedicalProfilesQuery.isFetching,
        deleteOrganizationMedicalProfile: deleteOrganizationMedicalProfileMutation.mutate,
        isDeletingOrganizationMedicalProfile: deleteOrganizationMedicalProfileMutation.isPending,
        organizationMedicalProfile: organizationMedicalProfileQuery.data || null,
        isLoadingOrganizationMedicalProfile: organizationMedicalProfileQuery.isLoading,
    };
}
